'use client';

import { useState, useEffect } from 'react';
import type { Restaurant } from '@/app/types/restaurant';
import { formatPrice, formatHours, isOpenNow, safeLabel } from '@/app/utils/format-helpers';
import { FavoriteButton } from '@/app/components/favorite-button';

// Category config: emoji + gradient per food type
const categoryConfig: Record<string, { emoji: string; gradient: string }> = {
  'banh mi': { emoji: '🥖', gradient: 'from-amber-300 to-orange-400' },
  'com':     { emoji: '🍚', gradient: 'from-orange-300 to-red-400' },
  'pho':     { emoji: '🍜', gradient: 'from-rose-300 to-pink-400' },
  'bun':     { emoji: '🍜', gradient: 'from-pink-300 to-rose-400' },
  'hu tieu': { emoji: '🍜', gradient: 'from-yellow-300 to-amber-400' },
  'che':     { emoji: '🍨', gradient: 'from-purple-300 to-pink-400' },
  'do uong': { emoji: '🥤', gradient: 'from-cyan-300 to-blue-400' },
  'lau':     { emoji: '🫕', gradient: 'from-red-400 to-orange-500' },
  'hai san': { emoji: '🦐', gradient: 'from-teal-300 to-cyan-400' },
  'an vat':  { emoji: '🍡', gradient: 'from-yellow-300 to-orange-400' },
  'nuong':   { emoji: '🍖', gradient: 'from-orange-400 to-red-500' },
  'banh':    { emoji: '🧁', gradient: 'from-pink-300 to-purple-400' },
  'chao':    { emoji: '🥣', gradient: 'from-yellow-200 to-amber-300' },
  'mi':      { emoji: '🍝', gradient: 'from-amber-300 to-yellow-400' },
  'ga':      { emoji: '🍗', gradient: 'from-orange-300 to-amber-400' },
  'bo':      { emoji: '🥩', gradient: 'from-red-300 to-rose-400' },
};

function getCategoryConfig(phanLoai: string): { emoji: string; gradient: string } {
  if (!phanLoai) return { emoji: '🍽️', gradient: 'from-coral-300 to-coral-500' };
  const lower = phanLoai.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  for (const [key, config] of Object.entries(categoryConfig)) {
    if (lower.includes(key)) return config;
  }
  return { emoji: '🍽️', gradient: 'from-coral-300 to-coral-500' };
}

// Build Google Maps directions URL from coordinates or address
function getDirectionsUrl(r: Restaurant): string {
  const destination = r.lat && r.lng
    ? `${r.lat},${r.lng}`
    : encodeURIComponent(r.dia_chi || '');
  return `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
}

type RestaurantCardProps = {
  restaurant: Restaurant;
  isSelected?: boolean;
  avgRating?: number;
  reviewCount?: number;
};

export function RestaurantCard({ restaurant: r, isSelected, avgRating, reviewCount }: RestaurantCardProps) {
  // Defer open/closed status to client to avoid SSR hydration mismatch (React #418)
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const open = mounted ? isOpenNow(r.gio_mo) : null;
  const { emoji, gradient } = getCategoryConfig(r.phan_loai);

  return (
    <div className={`bg-white rounded-xl p-3 md:p-4 shadow-sm transition-all duration-200 ${
      isSelected
        ? 'border-l-4 border-l-coral-500 border border-coral-200 shadow-md bg-cream-dark'
        : 'border border-coral-100 border-l-4 border-l-transparent hover:shadow-lg md:hover:-translate-y-1'
    }`}>
      <div className="flex gap-2 md:gap-3">
        {/* Gradient placeholder image — 48px mobile, 80px desktop */}
        <div className={`w-12 h-12 md:w-20 md:h-20 rounded-lg md:rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-2xl md:text-4xl flex-shrink-0 shadow-sm`}>
          {emoji}
        </div>

        {/* Details */}
        <div className="flex-1 min-w-0">
          {/* Row 1: Name + distance + actions */}
          <div className="flex items-center justify-between gap-1">
            <p className="font-display font-semibold text-sm md:text-base text-gray-800 truncate">{r.ten_quan}</p>
            <div className="flex items-center gap-0.5 md:gap-1 flex-shrink-0">
              {r.distance && (
                <span className="text-blue-700 font-medium text-xs bg-blue-50 px-1 md:px-1.5 py-0.5 rounded-full whitespace-nowrap">
                  {r.distance < 1000 ? `${r.distance}m` : `${(r.distance / 1000).toFixed(1)}km`}
                </span>
              )}
              <a
                href={getDirectionsUrl(r)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-500 hover:text-blue-700 transition-colors p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Chỉ đường đến quán"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 md:w-4 md:h-4">
                  <path d="M12 2C7.589 2 4 5.589 4 9.995 3.971 16.44 11.696 21.784 12 22c0 0 8.029-5.56 8-12.005C20 5.589 16.411 2 12 2Zm0 12c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4Z"/>
                </svg>
              </a>
              <FavoriteButton restaurantStt={r.stt} />
            </div>
          </div>

          {/* Row 2: Category + district + status + price */}
          <div className="flex items-center gap-1.5 md:gap-2 mt-0.5 flex-wrap">
            <span className="text-xs bg-coral-100 text-coral-900 px-1.5 py-0.5 rounded-full">
              {safeLabel(r.phan_loai)}
            </span>
            <span className="text-xs text-gray-600">{safeLabel(r.district)}</span>
            {open !== null && (
              <span className={`text-xs font-medium ${open ? 'text-green-700' : 'text-gray-600'}`}>
                {open ? '🟢 Mở' : '🔴 Đóng'}
              </span>
            )}
            {/* Price inline on mobile, separate row on desktop */}
            {r.gia && (
              <span className="text-xs bg-green-50 text-green-700 font-medium px-1.5 md:px-2 py-0.5 rounded-full md:hidden">
                {formatPrice(r.gia)}
              </span>
            )}
          </div>

          {/* Row 3: Rating (if exists) */}
          {avgRating != null && avgRating > 0 && (
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-yellow-500 text-xs md:text-sm">★</span>
              <span className="text-xs md:text-sm font-medium text-gray-700">{avgRating}</span>
              <span className="text-xs text-gray-600">({reviewCount})</span>
            </div>
          )}

          {/* Desktop only: address + hours + price */}
          <p className="hidden md:block text-xs text-gray-600 truncate mt-1">📍 {r.dia_chi}</p>
          <div className="hidden md:flex items-center justify-between mt-1">
            {r.gio_mo && <span className="text-xs text-gray-600">🕐 {formatHours(r.gio_mo)}</span>}
            {r.gia && (
              <span className="text-xs bg-green-50 text-green-700 font-medium px-2 py-0.5 rounded-full">
                {formatPrice(r.gia)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
